import React, { useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Share2, Gift, Lock, Sparkles } from "lucide-react";

/**
 * /account/rewards — customer's loyalty hub.
 * Wallet + daily check-in + referral share + scratch cards + ledger.
 */
export default function AccountRewards() {
  const [me, setMe] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [cards, setCards] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);
  const [revealingId, setRevealingId] = useState(null);
  const [flashCard, setFlashCard] = useState(null); // { id, coins } — just-revealed animation trigger

  const load = async () => {
    try {
      const [meR, lgR, scR] = await Promise.all([
        api.get("/loyalty/me"),
        api.get("/loyalty/ledger"),
        api.get("/loyalty/scratch-cards").catch(() => ({ data: { items: [] } })),
      ]);
      setMe(meR.data);
      setLedger(lgR.data.items || []);
      setCards(scR.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rewards");
    }
  };
  useEffect(() => { load(); }, []);

  if (!me) return <div className="p-6">Loading…</div>;
  if (!me.enabled) return (
    <div className="p-6 text-center text-neutral-500" data-testid="rewards-disabled">
      Loyalty program is not enabled on this store.
    </div>
  );

  const label = me.labels?.coin_plural || "Coins";
  const referralUrl = `${window.location.origin}${window.location.pathname.startsWith('/t/') ? window.location.pathname.split('/').slice(0,3).join('/') : ''}?ref=${me.referral_code}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Join me on ${me.labels?.program || "our rewards program"}`,
          text: `Sign up with my code ${me.referral_code} — we both get bonus ${label}!`,
          url: referralUrl,
        });
      } else {
        await navigator.clipboard.writeText(referralUrl);
        toast.success("Referral link copied!");
      }
    } catch { /* user cancelled */ }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(me.referral_code); toast.success("Code copied"); } catch {}
  };

  const checkin = async () => {
    setCheckingIn(true);
    try {
      const { data } = await api.post("/loyalty/checkin");
      toast.success(`+${data.coins_earned} ${label}${data.streak_bonus ? ` + ${data.streak_bonus} streak bonus!` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Already checked in today");
    }
    setCheckingIn(false);
  };

  const revealCard = async (card) => {
    if (card.status !== "pending" || revealingId) return;
    setRevealingId(card.id);
    try {
      const { data } = await api.post(`/loyalty/scratch-cards/${card.id}/reveal`);
      setFlashCard({ id: card.id, coins: data.coins });
      toast.success(`You won +${data.coins} ${label}!`);
      // Small delay so the reveal animation plays before we refetch.
      setTimeout(() => { setFlashCard(null); load(); }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal card");
    }
    setRevealingId(null);
  };

  const pendingCards = cards.filter((c) => c.status === "pending");
  const pastCards = cards.filter((c) => c.status !== "pending");

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="account-rewards-page">
      <h1 className="text-3xl font-display">{me.labels?.program || "Rewards"}</h1>

      {/* Wallet card */}
      <div className="bg-gradient-to-br from-amber-100 to-orange-50 border border-amber-200 rounded-xl p-6">
        <div className="text-xs uppercase tracking-wider text-amber-700">Your balance</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-4xl font-medium text-amber-900" data-testid="rewards-balance">{me.balance}</span>
          <span className="text-amber-800">{label}</span>
        </div>
        <div className="text-xs text-amber-800/70 mt-2">
          ≈ {money(me.balance * (me.coin_to_inr_ratio || 1))} redeemable at checkout ·
          Lifetime earned: {me.lifetime_earned}
        </div>
      </div>

      {/* Scratch cards — cashback earned on delivery */}
      {(me.cashback_enabled || cards.length > 0) && (
        <div className="border border-neutral-200 rounded-xl p-5" data-testid="scratch-cards-section">
          <div className="flex items-center gap-2 mb-3">
            <Gift size={18} className="text-amber-600" />
            <div className="font-medium">Cashback Scratch Cards</div>
          </div>
          {pendingCards.length === 0 && pastCards.length === 0 && (
            <div className="text-sm text-neutral-500">
              Place an order — once it&apos;s delivered you&apos;ll unlock a scratch card here.
            </div>
          )}
          {pendingCards.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              {pendingCards.map((c) => {
                const isFlashing = flashCard?.id === c.id;
                const isRevealing = revealingId === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => revealCard(c)}
                    disabled={isRevealing || isFlashing}
                    data-testid={`scratch-card-${c.id}`}
                    className={`relative aspect-[4/3] rounded-xl overflow-hidden transition-all ${
                      isFlashing
                        ? "scale-105 ring-4 ring-amber-400"
                        : "hover:scale-[1.02] active:scale-[0.98]"
                    }`}
                    style={{
                      background:
                        "repeating-linear-gradient(45deg,#d4d4d8 0 8px,#a1a1aa 8px 16px)",
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      {isFlashing ? (
                        <>
                          <Sparkles size={22} className="text-yellow-300 mb-1" />
                          <div className="text-3xl font-bold" data-testid={`card-reveal-${c.id}`}>
                            +{flashCard.coins}
                          </div>
                          <div className="text-xs uppercase tracking-widest">{label}</div>
                        </>
                      ) : (
                        <>
                          <Lock size={20} className="mb-1 opacity-90" />
                          <div className="text-sm font-medium">
                            {isRevealing ? "Revealing…" : "Tap to scratch"}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest mt-0.5 opacity-80">
                            Cashback reward
                          </div>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {pastCards.length > 0 && (
            <div className="text-xs text-neutral-500">
              Recently revealed ·{" "}
              <span data-testid="past-cards-count">{pastCards.length}</span> card
              {pastCards.length === 1 ? "" : "s"}
              <div className="flex flex-wrap gap-1 mt-2">
                {pastCards.slice(0, 6).map((c) => (
                  <span
                    key={c.id}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                      c.status === "revealed"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-neutral-100 text-neutral-500 border border-neutral-200 line-through"
                    }`}
                    data-testid={`past-card-${c.status}-${c.id}`}
                  >
                    {c.status === "revealed" ? `+${c.coins} ${label}` : `Expired`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Daily check-in */}
      <div className="border border-neutral-200 rounded-xl p-5 flex items-center justify-between">
        <div>
          <div className="font-medium">Daily check-in</div>
          <div className="text-sm text-neutral-500">Come back every day to earn bonus {label}</div>
        </div>
        <button
          onClick={checkin}
          disabled={checkingIn}
          className="px-5 py-2.5 bg-neutral-900 text-white rounded-full text-sm disabled:opacity-50"
          data-testid="checkin-btn"
        >
          {checkingIn ? "…" : "Check in"}
        </button>
      </div>

      {/* Refer & earn */}
      <div className="border border-neutral-200 rounded-xl p-5">
        <div className="font-medium">Refer & Earn</div>
        <div className="text-sm text-neutral-500 mb-3">
          Share your code — you get <b>{me.referral_reward}</b> {label} and your friend gets <b>{me.invitee_reward}</b> {label} when they place their first order.
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex-1 bg-neutral-100 border border-neutral-200 rounded-md px-3 py-2 font-mono text-lg tracking-wider select-all"
            data-testid="referral-code"
          >
            {me.referral_code}
          </div>
          <button
            onClick={copyCode}
            className="px-3 py-2 border border-neutral-200 rounded-md hover:bg-neutral-50"
            title="Copy code"
            data-testid="referral-copy"
          >
            <Copy size={16} />
          </button>
          <button
            onClick={share}
            className="px-4 py-2 bg-neutral-900 text-white rounded-md flex items-center gap-2 text-sm"
            data-testid="referral-share"
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Ledger */}
      <div className="border border-neutral-200 rounded-xl p-5">
        <div className="font-medium mb-3">Recent activity</div>
        {ledger.length === 0 && (
          <div className="text-sm text-neutral-500">No activity yet — earn coins by shopping, reviewing, or referring friends.</div>
        )}
        <div className="space-y-2">
          {ledger.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm border-b border-neutral-100 pb-2 last:border-0">
              <div>
                <div className="capitalize">{r.event.replace(/_/g, " ")}</div>
                <div className="text-xs text-neutral-500">{r.note || ""}</div>
              </div>
              <div className={`font-medium ${r.amount >= 0 ? "text-emerald-700" : "text-neutral-700"}`}>
                {r.amount >= 0 ? "+" : ""}{r.amount}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
